import React, { useState, Fragment, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import axios from "axios";
import * as Constants from "../../../constants";
import { getHearderAuth, getTrialPopupContent } from "@utils";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  UncontrolledTooltip,
} from "reactstrap";
import { toast } from "react-toastify";
import Avatar from "@components/avatar";
import { Check } from "react-feather";
import Popup from "./Popup";

const fetchEmployees = async () => {
  const { data } = await axios.get(
    `${Constants.REST_API_WORKSUITE_V3}employees/gets?search=`,
    {
      headers: getHearderAuth(),
    }
  );
  return data.data;
};

const fetchSelectEmp = async () => {
  const headers = getHearderAuth();
  const response = await axios.get(
    `${Constants.CONVERT_CLOODO}list-share-employee`,
    { headers }
  );
  return response.data;
};

const EmployeeSelector = () => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupContent, setPopupContent] = useState(getTrialPopupContent());
  const [shareId, setShareId] = useState(0);
  const {
    data: employees,
    isLoading,
    error,
  } = useQuery("employees", fetchEmployees);

  const modalBodyStyle = {
    overflowY: "auto",
    maxHeight: "400px",
    marginLeft: "30px",
    marginBottom: "30px",
  };

  const toggleModal = () => setIsOpen(!isOpen);

  const { data: selectemps, isLoading: isFetching } = useQuery(
    "selectemps",
    fetchSelectEmp
  );

  useEffect(() => {
    if (selectemps) {
      if (selectemps.data) {
        const ids = selectemps.data.split(",");
        setSelectedEmployees(ids);
      }
      if (selectemps.id) {
        setShareId(selectemps.id);
      }
    }
  }, [selectemps]);

  const handleCheckboxChange = (event, employeeId) => {
    if (event.target.checked) {
      setSelectedEmployees([...selectedEmployees, employeeId]);
    } else {
      setSelectedEmployees(selectedEmployees.filter((id) => id !== employeeId));
    }
  };

  const saveEmpToServer = async (empData) => {
    setIsPopupOpen(true);
    const headers = getHearderAuth();
    const formData = new FormData();
    for (const key in empData) {
      if (empData.hasOwnProperty(key)) {
        formData.append(key, empData[key]);
      }
    }
    const response = await axios.post(
      `${Constants.CONVERT_CLOODO}share-message`,
      formData,
      { headers }
    );
    // console.log(response.data)
    if (!response.data.status) {
      throw new Error("Error");
    }
    return response.data;
  };

  const mutation = useMutation(saveEmpToServer, {
    onSuccess: (data) => {
      // On successful mutation
      if (data.id) {
        setShareId(data.id);
      }

      toast.success(
        <Fragment>
          <div className="toastify-header">
            <div className="title-wrapper">
              <Avatar size="sm" color="success" icon={<Check size={12} />} />
              <h6 className="toast-title">Updated successfully!</h6>
            </div>
          </div>
        </Fragment>,
        { icon: false, hideProgressBar: true }
      );

      queryClient.invalidateQueries("selectemps");
    },
    onError: (error) => {
      // setBlock(false)
      // On failed mutation
      toast.error(
        <Fragment>
          <div className="toastify-header">
            <div className="title-wrapper">
              <Avatar size="sm" color="danger" icon={<X size={12} />} />
              <h6 className="text-danger ms-50 mb-0">Error</h6>
            </div>
          </div>
          <div className="toastify-body">
            <span>An error occurred</span>
          </div>
        </Fragment>,
        {
          icon: false,
          autoClose: 3000,
          hideProgressBar: true,
        }
      );
    },
  });

  const handleSubmit = () => {
    console.log(selectedEmployees);
    mutation.mutate({ employees: selectedEmployees, id: shareId });
    // toggleModal();
  };

  if (isLoading)
    return (
      <div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="2em"
          height="2em"
          viewBox="0 0 24 24"
        >
          <rect width="24" height="24" fill="none" />
          <path
            fill="currentColor"
            d="M12 2A10 10 0 1 0 22 12A10 10 0 0 0 12 2Zm0 18a8 8 0 1 1 8-8A8 8 0 0 1 12 20Z"
            opacity="0.5"
          />
          <path
            fill="currentColor"
            d="M20 12h2A10 10 0 0 0 12 2V4A8 8 0 0 1 20 12Z"
          >
            <animateTransform
              attributeName="transform"
              dur="1s"
              from="0 12 12"
              repeatCount="indefinite"
              to="360 12 12"
              type="rotate"
            />
          </path>
        </svg>
      </div>
    );
  if (error)
    return (
      <>
        <Button.Ripple
          outline
          color="danger"
          size="sm"
          id="el-tooltip-share-chat"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="25"
            height="25"
            viewBox="0 0 36 36"
          >
            <rect width="36" height="36" fill="none" />
            <path
              fill="currentColor"
              d="M18.42 16.31a5.7 5.7 0 1 1 5.76-5.7a5.74 5.74 0 0 1-5.76 5.7m0-9.4a3.7 3.7 0 1 0 3.76 3.7a3.74 3.74 0 0 0-3.76-3.7"
            />
            <path
              fill="currentColor"
              d="M18.42 16.31a5.7 5.7 0 1 1 5.76-5.7a5.74 5.74 0 0 1-5.76 5.7m0-9.4a3.7 3.7 0 1 0 3.76 3.7a3.74 3.74 0 0 0-3.76-3.7m3.49 10.74a20.6 20.6 0 0 0-13 2a1.77 1.77 0 0 0-.91 1.6v3.56a1 1 0 0 0 2 0v-3.43a18.92 18.92 0 0 1 12-1.68Z"
            />
            <path
              fill="currentColor"
              d="M33 22h-6.7v-1.48a1 1 0 0 0-2 0V22H17a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V23a1 1 0 0 0-1-1m-1 10H18v-8h6.3v.41a1 1 0 0 0 2 0V24H32Z"
            />
            <path
              fill="currentColor"
              d="M21.81 27.42h5.96v1.4h-5.96zM10.84 12.24a18 18 0 0 0-7.95 2A1.67 1.67 0 0 0 2 15.71v3.1a1 1 0 0 0 2 0v-2.9a16 16 0 0 1 7.58-1.67a7.28 7.28 0 0 1-.74-2m22.27 1.99a17.8 17.8 0 0 0-7.12-2a7.46 7.46 0 0 1-.73 2A15.89 15.89 0 0 1 32 15.91v2.9a1 1 0 1 0 2 0v-3.1a1.67 1.67 0 0 0-.89-1.48m-22.45-3.62v-.67a3.07 3.07 0 0 1 .54-6.11a3.15 3.15 0 0 1 2.2.89a8.16 8.16 0 0 1 1.7-1.08a5.13 5.13 0 0 0-9 3.27a5.1 5.1 0 0 0 4.7 5a7.42 7.42 0 0 1-.14-1.3m14.11-8.78a5.17 5.17 0 0 0-3.69 1.55a7.87 7.87 0 0 1 1.9 1a3.14 3.14 0 0 1 4.93 2.52a3.09 3.09 0 0 1-1.79 2.77a7.14 7.14 0 0 1 .06.93a7.88 7.88 0 0 1-.1 1.2a5.1 5.1 0 0 0 3.83-4.9a5.12 5.12 0 0 0-5.14-5.07"
            />
          </svg>
        </Button.Ripple>
        <UncontrolledTooltip placement="top" target="el-tooltip-share-chat">
          Error loading employees. Please try again later.
        </UncontrolledTooltip>
      </>
    );

  return (
    <>
      <Button.Ripple
        outline
        color="primary"
        size="sm"
        id="el-tooltip-share-chat"
        onClick={toggleModal}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="25"
          height="25"
          viewBox="0 0 36 36"
        >
          <rect width="36" height="36" fill="none" />
          <path
            fill="currentColor"
            d="M18.42 16.31a5.7 5.7 0 1 1 5.76-5.7a5.74 5.74 0 0 1-5.76 5.7m0-9.4a3.7 3.7 0 1 0 3.76 3.7a3.74 3.74 0 0 0-3.76-3.7"
          />
          <path
            fill="currentColor"
            d="M18.42 16.31a5.7 5.7 0 1 1 5.76-5.7a5.74 5.74 0 0 1-5.76 5.7m0-9.4a3.7 3.7 0 1 0 3.76 3.7a3.74 3.74 0 0 0-3.76-3.7m3.49 10.74a20.6 20.6 0 0 0-13 2a1.77 1.77 0 0 0-.91 1.6v3.56a1 1 0 0 0 2 0v-3.43a18.92 18.92 0 0 1 12-1.68Z"
          />
          <path
            fill="currentColor"
            d="M33 22h-6.7v-1.48a1 1 0 0 0-2 0V22H17a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V23a1 1 0 0 0-1-1m-1 10H18v-8h6.3v.41a1 1 0 0 0 2 0V24H32Z"
          />
          <path
            fill="currentColor"
            d="M21.81 27.42h5.96v1.4h-5.96zM10.84 12.24a18 18 0 0 0-7.95 2A1.67 1.67 0 0 0 2 15.71v3.1a1 1 0 0 0 2 0v-2.9a16 16 0 0 1 7.58-1.67a7.28 7.28 0 0 1-.74-2m22.27 1.99a17.8 17.8 0 0 0-7.12-2a7.46 7.46 0 0 1-.73 2A15.89 15.89 0 0 1 32 15.91v2.9a1 1 0 1 0 2 0v-3.1a1.67 1.67 0 0 0-.89-1.48m-22.45-3.62v-.67a3.07 3.07 0 0 1 .54-6.11a3.15 3.15 0 0 1 2.2.89a8.16 8.16 0 0 1 1.7-1.08a5.13 5.13 0 0 0-9 3.27a5.1 5.1 0 0 0 4.7 5a7.42 7.42 0 0 1-.14-1.3m14.11-8.78a5.17 5.17 0 0 0-3.69 1.55a7.87 7.87 0 0 1 1.9 1a3.14 3.14 0 0 1 4.93 2.52a3.09 3.09 0 0 1-1.79 2.77a7.14 7.14 0 0 1 .06.93a7.88 7.88 0 0 1-.1 1.2a5.1 5.1 0 0 0 3.83-4.9a5.12 5.12 0 0 0-5.14-5.07"
          />
        </svg>
      </Button.Ripple>
      <UncontrolledTooltip placement="top" target="el-tooltip-share-chat">
        Share WhatsApp messages from your phone number with employees
      </UncontrolledTooltip>

      <Modal isOpen={isOpen} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>
          Select Employees to View Shared WhatsApp Messages
        </ModalHeader>
        <ModalBody style={modalBodyStyle}>
          {employees.map((employee) => (
            <>
              <FormGroup
                key={employee.id}
                check
                style={{
                  padding: "10px",
                  background: selectedEmployees.includes(employee.id)
                    ? "#f0f0f0"
                    : "none",
                }}
              >
                <Label
                  check
                  style={{
                    width: "100%",
                    margin: "0",
                    cursor: "pointer",
                    borderRadius: "5px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Input
                    type="checkbox"
                    value={employee.id}
                    onChange={(event) =>
                      handleCheckboxChange(event, employee.id)
                    }
                    checked={selectedEmployees.includes(employee.id)}
                  />{" "}
                  <span
                    style={{
                      alignItems: "center",
                      color: "#5E5873",
                      marginLeft: "15px",
                    }}
                  >
                    <img
                      src={employee.image_url}
                      alt=""
                      style={{
                        width: 30,
                        height: 30,
                        marginRight: 10,
                        borderRadius: "50%",
                      }}
                    />
                    {employee.name}
                  </span>
                </Label>
              </FormGroup>
              <div style={{ border: "1px solid #F8F8F8" }}></div>
            </>
          ))}
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={handleSubmit}
            disabled={employees.length === 0}
          >
            Save
          </Button>{" "}
          <Button color="secondary" onClick={toggleModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      <Popup
        isOpen={isPopupOpen}
        setIsOpen={setIsPopupOpen}
        title={popupContent.title}
        description={popupContent.description}
        buttonText={popupContent.buttonText}
        buttonOnClick={popupContent.buttonOnClick}
      />
    </>
  );
};

export default EmployeeSelector;
